import React from 'react';
import PropTypes from 'prop-types';
import { dialogs, i18next, utils } from '@yola/ws-sdk';
import { Modal, Panel, PanelGroup, designSystem, reactHookForm } from '@yola/ws-ui';

const {
  InputGroupField,
  FieldStatus,
  Stack,
  ControlGroup,
  Box,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
} = designSystem;

const { useForm } = reactHookForm;
const MODAL_WIDTH = 483;

const AddImageFromUrlDialog = ({ onDialogMainAction, onDialogCancel }) => {
  const { control, handleSubmit } = useForm({
    mode: 'onTouched',
    defaultValues: {
      imageUrl: '',
    },
  });

  const captions = {
    title: i18next.t('Add from URL'),
    submitButton: i18next.t('Submit'),
    cancelButton: i18next.t('Cancel'),
    imageUrl: i18next.t('Image URL'),
    helperText: i18next.t('Paste a direct link to an image to fetch it from URL'),
    invalidUrl: i18next.t('Invalid URL. Please, check the URL and try again'),
  };

  const validateUrl = (value) => {
    if (!value || !value.trim()) {
      return captions.invalidUrl;
    }

    if (!utils.validation.isURL(value.trim())) {
      return captions.invalidUrl;
    }

    return null;
  };

  const handleDialogSubmit = ({ imageUrl }) => {
    const trimmedImageUrl = imageUrl.trim();
    dialogs.operations.hide({ preserveSharedData: true });
    onDialogMainAction(trimmedImageUrl);
  };

  const handleCancel = () => {
    dialogs.operations.hide({ preserveSharedData: true });
    onDialogCancel();
  };

  return (
    <Modal
      width={MODAL_WIDTH}
      height="auto"
      centered
      resizable={false}
      draggable={false}
      onOverlayClick={handleCancel}
      onPressEsc={handleCancel}
      overlay="visible"
      isContainerScrollable
      offsetX={0}
      offsetY={0}
    >
      <PanelGroup height="100%">
        <Panel
          theme="gray-100"
          corners="squared"
          align="middle"
          style={{ flexShrink: '0', paddingLeft: 0 }}
          height="100%"
          className="ws-modal__content"
        >
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel theme="white" style={{ borderBottom: 'none' }} height="100%" scrollable>
          <Box
            paddingTop="spacing-l"
            paddingBottom="spacing-l"
            paddingLeft="spacing-l"
            paddingRight="spacing-l"
          >
            <Stack gap="spacing-4-xs">
              <ControlGroup title={captions.imageUrl}>
                <InputGroupField
                  type="url"
                  name="imageUrl"
                  control={control}
                  placeholder="https://"
                  rules={{
                    validate: validateUrl,
                  }}
                  autoComplete="off"
                />
              </ControlGroup>
              <FieldStatus text={captions.helperText} />
            </Stack>
          </Box>
        </Panel>
        <Panel theme="white" style={{ flexShrink: '0' }} height="100%">
          <DialogButtonGroup>
            <DialogButton onClick={handleSubmit(handleDialogSubmit)}>
              {captions.submitButton}
            </DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={handleCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

AddImageFromUrlDialog.propTypes = {
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AddImageFromUrlDialog;
